import Grid from '@mui/material/Grid';
import { useEffect, useState, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import { UAR_USER_PROFILE_FIELDS, UAR_USER_PROFILE_KEYS } from './_const';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Autocomplete from '@mui/material/Autocomplete';
import StaffMemberInformationCard from 'pages/AnnualUserAccessReview/components/StaffMemberInformation/StaffMemberInformationCard';
import { WQAccordion } from 'components/UI/WQAccordion/WQAccordion';
import { Checkbox } from '@upmc/ims.component-library';
import ThirdPartyOrganization from 'pages/NewNonEmployee/components/fields/ThirdPartyOrganization';

const FormFields = ({
	section,
	formik,
	validationSchema,
	userData,
	isEmployee,
	...props
}) => {
	const [options, setOptions] = useState([]);
	const [fields, setFields] = useState();

	const filteredFields = Object.fromEntries(
		Object.entries(UAR_USER_PROFILE_FIELDS).filter(
			([key, value]) => value.section === section
		)
	);

	const dependentFields = Object.entries(UAR_USER_PROFILE_FIELDS)
		.filter(([key, value]) => value.prePopulateDependency !== undefined)
		.map(([key, value]) => value.prePopulateDependency);

	const dependentFormikValues = JSON.stringify(
		dependentFields.map(key => formik.values[key])
	);
	// Selects just the fields for that particular wizard section
	const selectFields = useCallback(async () => {
		// If it has a prePopulate API to call, it calls the API and sets the data to prePopulateData field
		// Check each field in filteredFields
		for (let field of Object.keys(filteredFields)) {
			// If the field has a 'prePopulate' property
			if (filteredFields[field].hasOwnProperty('prePopulate')) {
				let dependency =
					formik.values[
						filteredFields[field]['prePopulateDependency']
					];
				// Add 'prePopulateData' property with the data returned from 'prePopulate'
				if (
					filteredFields[field].hasOwnProperty(
						'prePopulateDependency'
					) &&
					dependency
				) {
					filteredFields[field]['prePopulateData'] =
						await filteredFields[field].prePopulate(dependency);
				} else if (
					filteredFields[field].hasOwnProperty('dependentOn')
				) {
					// Allows to pass a parameter to the API call via the json const file
					let prePopulateParam =
						filteredFields[field]['prePopulateParam'];
					filteredFields[field]['prePopulateData'] = prePopulateParam
						? await filteredFields[field].prePopulate(
								prePopulateParam
						  )
						: await filteredFields[field].prePopulate();
				} else {
					filteredFields[field]['prePopulateData'] =
						await filteredFields[field].prePopulate();
				}
			}
		}
		return filteredFields;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [section, dependentFormikValues]); // dependencies

	useEffect(() => {
		selectFields().then(result => setFields(result));
	}, [selectFields]);

	// Check to see if the field is required by checking validation schema
	// This is mostly for the validation when fields are dependent on other fields
	const isFieldRequiredViaValidationSchema = fieldName => {
		const fieldSchema = validationSchema.fields[fieldName];

		const hasRequired = fieldSchema?.tests?.some(
			test => test.OPTIONS.name === 'required'
		);
		return hasRequired;
	};

	// Check if field is required from the const fields object
	const isFieldRequired = fieldName => {
		let isRequired = UAR_USER_PROFILE_FIELDS[fieldName]?.required
			? UAR_USER_PROFILE_FIELDS[fieldName]?.when(
					formik.values[
						UAR_USER_PROFILE_FIELDS[fieldName]?.dependentOn
					]
			  )
			: false;

		return isRequired;
	};

	const showField = fieldName => {
		const fieldProfile = UAR_USER_PROFILE_FIELDS[fieldName];

		if (!fieldProfile) {
			return false;
		}

		const { required, dependentOn, when } = fieldProfile;

		// If 'required' field is not present, return true
		if (!required) {
			return true;
		}

		if (!required || !dependentOn) {
			return false;
		}

		const dependentValue = formik.values[dependentOn];

		return when(dependentValue);
	};

	// Check if field requires autocomplete
	const isFieldAutoComplete = fieldName => {
		let isAutoComplete = UAR_USER_PROFILE_FIELDS[fieldName]?.autoComplete
			? true
			: false;
		return isAutoComplete;
	};

	// Check if form is touched and has errors
	const hasErrors = name => {
		return name in formik.touched && name in formik.errors;
	};

	const firstName = formik?.values['User to Edit']?.[0]?.firstName ?? '';
	const lastName = formik?.values['User to Edit']?.[0]?.lastName ?? '';

	const esoFiId = formik?.values['User to Edit']?.[0].fiId;

	const fieldsToDisabled = [
		UAR_USER_PROFILE_KEYS.JOB_TITLE,
		UAR_USER_PROFILE_KEYS.COST_CENTER,
	];

	// When the country changes, reset the state
	const ResetStateValue = key => {
		if (key === UAR_USER_PROFILE_KEYS.COUNTRY) {
			formik.setFieldValue(UAR_USER_PROFILE_KEYS.STATE_PROVINCE, '');
		}
		if (key === UAR_USER_PROFILE_KEYS.HOME_COUNTRY) {
			formik.setFieldValue(UAR_USER_PROFILE_KEYS.HOME_STATE, '');
		}
	};

	// When the cost center changes, reset the department
	const ResetDepartmentValue = key => {
		if (key === UAR_USER_PROFILE_KEYS.COST_CENTER) {
			formik.setFieldValue(UAR_USER_PROFILE_KEYS.DEPARTMENT, '');
		}
	};

	// When the cost center changes, reset the business unit
	const ResetBusinessUnit = key => {
		if (key === UAR_USER_PROFILE_KEYS.COST_CENTER) {
			formik.setFieldValue(UAR_USER_PROFILE_KEYS.BUSINESS_UNIT, '');
		}
	};

	return (
		<Grid container spacing={5} p={5}>
			<Grid item xs={12}>
				<WQAccordion coloredHeader title={firstName + ' ' + lastName}>
					<StaffMemberInformationCard
						staffMember={{ esoFiId, firstName, lastName }}
					/>
				</WQAccordion>
			</Grid>
			{fields &&
				Object.entries(fields).map(
					([key, field]) =>
						showField(key) && (
							<Grid item xs={6} key={key}>
								<>
									<InputLabel id='demo-simple-select-label'>
										{isFieldRequiredViaValidationSchema(
											key
										) || isFieldRequired(key)
											? '* '
											: null}
										{field.stepLabel}
									</InputLabel>

									{field.type === 'select' ? (
										<FormControl>
											<Select
												required={
													isFieldRequiredViaValidationSchema(
														key
													) || isFieldRequired(key)
												}
												disabled={
													fieldsToDisabled?.includes(
														key
													) && isEmployee
												}
												key={key}
												name={key}
												fullWidth
												displayEmpty
												onBlur={formik.handleBlur}
												variant='outlined'
												id={'select--' + key}
												value={formik.values[key]}
												onChange={e => {
													formik.handleChange(e);
													ResetStateValue(key);
												}}
												error={hasErrors(key)}
											>
												<MenuItem disabled value=''>
													<em>
														Select {field.stepLabel}
													</em>
												</MenuItem>
												{formik.values[key] &&
													!field?.prePopulateData?.some(
														item =>
															item.Value ===
																formik.values[
																	key
																] ||
															item ===
																formik.values[
																	key
																]
													) && (
														<MenuItem
															value={
																formik.values[
																	key
																]
															}
														>
															{formik.values[key]}
														</MenuItem>
													)}
												{field?.prePopulateData?.map(
													(item, index) => (
														<MenuItem
															key={
																item.Key ?? item
															}
															value={
																item.Value ??
																item
															}
														>
															{item.Value ?? item}
														</MenuItem>
													)
												)}
											</Select>

											<FormHelperText>
												{hasErrors(key)
													? formik.errors[key]
													: ''}
											</FormHelperText>
										</FormControl>
									) : isFieldAutoComplete(key) ? (
										<Autocomplete
											fullWidth
											id='combo-box'
											options={options}
											value={formik.values[key]}
											getOptionLabel={option => option}
											freeSolo={false} // Restrict input to provided options
											onChange={(event, newValue) => {
												ResetDepartmentValue(key);
												ResetBusinessUnit(key);
												formik.setFieldValue(
													key,
													newValue
												);
											}}
											onInputChange={(
												event,
												newValue
											) => {
												if (
													newValue !==
													formik.values[key]
												) {
													let tempOptions =
														UAR_USER_PROFILE_FIELDS[
															key
														].autoCompleteAPI(
															newValue
														);

													tempOptions.then(
														results => {
															setOptions(
																results?.map(
																	item =>
																		item.trim()
																)
															);
														}
													);
												}
											}}
											onBlur={() => {
												if (options.length > 0) {
													setOptions([]);
												}
											}}
											renderInput={params => (
												<>
													<TextField
														{...params}
														key={key}
														required={
															isFieldRequiredViaValidationSchema(
																key
															) ||
															isFieldRequired(key)
														}
														fullWidth
														onBlur={
															formik.handleBlur
														}
														name={key}
														type={field.type}
														placeholder={`Type to search for ${field.stepLabel}`}
														error={hasErrors(key)}
														helperText={
															hasErrors(key)
																? formik.errors[
																		key
																  ]
																: ''
														}
													/>
												</>
											)}
										/>
										) : field.type === 'checkbox' ? (
										<Checkbox
											size='medium'
											key={key}
											checked={formik.values[key] || false}
											disabled={
												key ===
												UAR_USER_PROFILE_KEYS.JOB_TITLE &&
												formik.values[
													UAR_USER_PROFILE_KEYS.TYPEID
												].toString() === '7'
											}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											value={formik.values[key]}
											name={key}
										/>
										) : field.type === 'thirdPartyOrganizationAutoComplete' ? (
										<ThirdPartyOrganization
											showNewOrgCreation={false}
											label=""
											required={false}
											displayEmpty
										/>
										) : (
										<TextField
											required={
												isFieldRequiredViaValidationSchema(
													key
												) || isFieldRequired(key)
											}
											key={key}
											fullWidth
											disabled={
												key ===
													UAR_USER_PROFILE_KEYS.JOB_TITLE &&
												formik.values[
													UAR_USER_PROFILE_KEYS.TYPEID
												].toString() === '7'
											}
											// variant='outlined'
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											value={formik.values[key]}
											name={key}
											type={field.type}
											// label={field.stepLabel}
											error={hasErrors(key)}
											helperText={
												hasErrors(key)
													? formik.errors[key]
													: ''
											}
										/>
									)}
								</>
							</Grid>
						)
				)}
		</Grid>
	);
};

export default FormFields;
